import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Page } from "@/components/Layout";
import { api } from "@/lib/api";
import { useProSignup } from "@/components/ProfessionalSignupModal";
import { useCurrency } from "@/lib/currency";
import Seo from "@/components/Seo";
import { MagnifyingGlass, ArrowRight, X, Compass, GraduationCap, CheckCircle, ChatCircle, Clock, GlobeHemisphereWest, ChatsCircle, Brain, BookOpen } from "@phosphor-icons/react";
import IntroAudioPlayer from "@/components/IntroAudioPlayer";
import IntroAudioSummary from "@/components/IntroAudioSummary";
import DirectoryHelpBand from "@/components/DirectoryHelpBand";

const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const audioSrc = (u) => {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return `${BACKEND}${u}`;
};

const pathwayGroups = [
  {
    label: "Study Level",
    color: "var(--md-primary)",
    icon: GraduationCap,
    tags: ["School & Pre-University", "Undergraduate", "Master's", "PhD / Doctoral Training", "Postdoctoral & Early Career", "Career Transition"],
  },
  {
    label: "Country / Region",
    color: "var(--md-secondary-dark)",
    icon: GlobeHemisphereWest,
    tags: ["India", "United Kingdom", "United States", "Canada", "Australia", "Europe Region", "Middle East", "Other"],
  },
  {
    label: "Session Type",
    color: "var(--md-accent-hover, #C29A1F)",
    icon: ChatsCircle,
    tags: ["Semester Wellbeing Session", "Career Strategy & Development", "Application Construction", "Application Review (CV / Supporting Statement)", "Mock Interview", "Cultural Adaptation & Integration", "Supporting Your Child's Journey Abroad"],
  },
  {
    label: "Psychology Discipline",
    color: "var(--md-primary)",
    icon: Brain,
    wide: true,
    tags: ["Clinical Psychology", "Counselling Psychology", "Neuropsychology", "Cognitive Neuroscience", "Research Psychology", "Organisational Psychology", "Educational Psychology", "Forensic Psychology", "Health Psychology", "Sport Psychology", "Other emerging fields"],
  },
];

export default function Mentors() {
  const [items, setItems] = useState([]);
  const [pathway, setPathway] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const { open: openProSignup } = useProSignup();
  const { format } = useCurrency();

  useEffect(() => {
    let alive = true;
    const fetchData = async () => {
      setLoading(true);
      const params = {};
      if (pathway) params.pathway = pathway;
      if (q) params.q = q;
      try {
        const r = await api.get("/mentors", { params });
        if (alive) setItems(r.data);
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchData();
    return () => { alive = false; };
  }, [pathway, q]);

  return (
    <Page>
      <Seo title="Find A Mentor · MindDhara Forward" description="Browse and book directly with vetted mentors at MindDhara, for academics, study abroad, careers and life pivots." />

      {/* HERO */}
      <section className="therapist-hero" data-testid="mentors-hero">
        <div className="md-container">
          <div className="therapist-hero-grid">
            <div className="therapist-hero-copy">
              <div className="md-label-eyebrow mb-3">Forward · Mentor Directory</div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif mb-5" style={{ lineHeight: 1.05 }}>
                Browse mentors<br /><span style={{ color: "var(--md-primary)" }}>Book directly</span>
              </h1>
              <p className="text-base md:text-lg max-w-xl" style={{ color: "var(--md-text-soft)" }}>
                Psychology mentorship from people who&apos;ve been there. Connect with psychologists, researchers, academics, and entrepreneurs from diverse areas of psychology who have studied, trained, and worked around the world.
              </p>

              <div className="therapist-search-wrap mt-7" data-testid="mentor-search-wrap">
                <MagnifyingGlass size={18} className="therapist-search-ico" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by name or pathway…"
                  className="therapist-search-input"
                  data-testid="mentor-search-input"
                />
                {q && (
                  <button onClick={() => setQ("")} className="therapist-search-clear" aria-label="Clear search" data-testid="mentor-search-clear">
                    <X size={14} weight="bold" />
                  </button>
                )}
              </div>

              <div className="therapist-hero-stats">
                <span><strong>{items.length}</strong> Mentors</span>
                <span aria-hidden>·</span>
                <span>8 Pathways</span>
              </div>
            </div>

            <aside className="therapist-hero-card" data-testid="mentor-pro-cta">
              <div className="md-label-eyebrow mb-2" style={{ color: "var(--md-secondary)" }}>For Mentors</div>
              <h3 className="text-2xl font-serif" style={{ color: "var(--md-bg)" }}>Mentor with MindDhara</h3>
              <p className="text-sm mt-2.5" style={{ color: "rgba(251,250,247,0.78)" }}>
                Share your craft on a platform that handles bookings, payments and follow-ups, so you can focus on the mentee.
              </p>
              <div className="flex flex-nowrap items-center gap-2 mt-5">
                <button type="button" onClick={openProSignup} className="btn-accent text-sm" style={{ padding: "0.6rem 0.85rem", whiteSpace: "nowrap" }} data-testid="mentor-hero-apply-cta">
                  Apply to mentor <ArrowRight size={14} weight="bold" />
                </button>
                <a
                  href="https://docs.google.com/spreadsheets/d/1K_17scX51klWOGqwHbu02hcnk5O9JbldKvCqYkbWqqQ/edit?gid=0#gid=0"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline md-career-outline-light text-sm"
                  style={{ padding: "0.6rem 0.85rem", whiteSpace: "nowrap" }}
                  data-testid="mentor-psychology-resources-btn"
                >
                  <BookOpen size={14} weight="bold" /> Psychology Resources
                </a>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* FILTERS */}
      <section className="md-container therapist-filters" data-testid="mentor-filters">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div>
            <div className="md-label-eyebrow mb-1">Filter by pathway</div>
            <h2 className="text-lg font-serif">Where would you like guidance?</h2>
          </div>
          {pathway && (
            <button onClick={() => setPathway("")} className="btn-outline text-xs" data-testid="mentor-clear-pathway">
              Clear filter <X size={12} weight="bold" />
            </button>
          )}
        </div>

        <div className="therapist-tag-groups four-up">
          {pathwayGroups.map((g) => (
            <div key={g.label} className={`therapist-tag-group ${g.wide ? "therapist-tag-group-wide" : ""}`} data-testid={`pathway-group-${g.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="therapist-tag-group-head" style={{ color: g.color }}>
                <g.icon size={18} weight="duotone" />
                <span>{g.label}</span>
              </div>
              <div className="therapist-tag-chips">
                {g.tags.map((t) => {
                  const active = pathway === t;
                  return (
                    <button
                      key={t}
                      onClick={() => setPathway(active ? "" : t)}
                      className={`therapist-chip ${active ? "therapist-chip-active" : ""}`}
                      data-testid={`mentor-pathway-${t.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* LIST */}
      <section className="md-container" data-testid="mentor-list-section">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <h2 className="text-2xl font-serif">
            {pathway ? <>Mentors for <span style={{ color: "var(--md-primary)" }}>{pathway}</span></> : "All mentors"}
          </h2>
          {!loading && <span className="text-sm" style={{ color: "var(--md-text-muted)" }}>{items.length} results</span>}
        </div>

        {(() => {
          if (loading) {
            return (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => <div key={i} className="md-card therapist-skel" />)}
              </div>
            );
          }
          if (items.length === 0) {
            return (
              <div className="md-card text-center py-12" data-testid="mentor-empty">
                <h3 className="text-xl font-serif mb-2">No matches for this pathway</h3>
                <p className="text-sm" style={{ color: "var(--md-text-soft)" }}>Try a different pathway or clear the filter to see everyone.</p>
                {pathway && <button className="btn-outline text-sm mt-5" onClick={() => setPathway("")}>Show all mentors</button>}
              </div>
            );
          }
          return (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="mentor-grid">
            {items.map((m) => (
              <Link key={m.id} to={`/mentors/${m.id}?trail=mentors`} className="md-card md-card-hover therapist-card" data-testid={`mentor-card-${m.id}`}>
                <div className="flex gap-4">
                  {m.photo_url ? (
                    <img src={m.photo_url} alt={m.name} className="w-16 h-16 rounded-full object-cover therapist-card-photo" />
                  ) : (
                    <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: "var(--md-bg-alt)" }}>
                      <Compass size={22} weight="duotone" style={{ color: "var(--md-primary)" }} />
                    </div>
                  )}
                  <div className="min-w-0">
                    <h3 className="font-serif text-xl truncate">{m.name}</h3>
                    <p className="text-sm truncate" style={{ color: "var(--md-text-soft)" }}>{m.title}</p>
                    <div className="therapist-card-badges" data-testid={`mentor-badges-${m.id}`}>
                      {Number(m.experience_years) >= 5 && (
                        <span className="therapist-card-badge">{m.experience_years}+ yrs</span>
                      )}
                      {m.approved !== false && (
                        <span className="therapist-card-badge therapist-card-badge-verified"><CheckCircle size={10} weight="fill" /> Verified</span>
                      )}
                    </div>
                  </div>
                </div>
                <p className="mt-4 text-sm break-words" style={{ color: "var(--md-text-soft)", overflowWrap: "anywhere" }}>{(m.bio || "").slice(0, 110)}…</p>
                <div className="flex flex-wrap gap-1.5 mt-4">
                  {(m.pathways || []).slice(0, 3).map((s) => (
                    <span key={s} className="therapist-card-pill">{s}</span>
                  ))}
                </div>
                {(m.languages?.length || m.next_available_slot) && (
                  <div className="therapist-card-meta-row" data-testid={`mentor-meta-${m.id}`}>
                    {m.languages?.length > 0 && (
                      <span className="therapist-card-meta-chip" data-testid={`mentor-langs-${m.id}`}>
                        <ChatCircle size={12} weight="duotone" /> {m.languages.slice(0, 3).join(", ")}
                      </span>
                    )}
                    {m.next_available_slot && (
                      <span className="therapist-card-meta-chip therapist-card-meta-chip-accent" data-testid={`mentor-next-${m.id}`}>
                        <Clock size={12} weight="duotone" /> Next available: {m.next_available_slot}
                      </span>
                    )}
                  </div>
                )}
                {m.intro_audio_url && (
                  <IntroAudioPlayer src={audioSrc(m.intro_audio_url)} testid={m.id} />
                )}
                <IntroAudioSummary summary={m.intro_audio_summary} testid={m.id} />
                <div className="flex items-center justify-end mt-5 pt-4 border-t" style={{ borderColor: "var(--md-border)" }}>
                  <div className="text-sm font-medium" style={{ color: "var(--md-primary)" }}>
                    From {format(m.session_fee || 0)} <ArrowRight size={12} weight="bold" className="inline" />
                  </div>
                </div>
              </Link>
            ))}
            </div>
          );
        })()}
      </section>
      <DirectoryHelpBand kind="mentor" />
    </Page>
  );
}
